import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ClipboardList, CalendarDays, Layers, Activity, AlertTriangle,
  ShieldCheck, Copy, Ghost, RefreshCw, Loader2, ChevronDown, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const METRICS = [
  { key: 'prescriptions_total', label: 'Prescriptions', icon: ClipboardList, tone: 'zinc',
    detail: 'Total prescriptions in DB (templates + scheduled)' },
  { key: 'scheduled_prescriptions', label: 'Scheduled Prescriptions', icon: CalendarDays, tone: 'blue',
    detail: 'Prescriptions with date or week_start; save_as_template=false' },
  { key: 'canonical_sessions', label: 'Canonical Sessions', icon: Layers, tone: 'emerald',
    detail: 'db.sessions where source=PRESCRIPTION and is_active=true' },
  { key: 'planner_sessions', label: 'Planner Sessions', icon: CalendarDays, tone: 'indigo',
    detail: 'All active db.sessions surfaced to Planner' },
  { key: 'rtp_sessions', label: 'RTP Sessions', icon: Activity, tone: 'amber',
    detail: 'Sessions where type=RTP or program_type=RTP' },
];

const ISSUES = [
  { key: 'sessions_without_assignments', label: 'Sessions without Assignments', icon: AlertTriangle, tone: 'rose' },
  { key: 'sessions_without_planner',     label: 'Sessions without Planner', icon: Ghost, tone: 'rose', flat: true },
  { key: 'rtp_without_profile',          label: 'RTP without Profile', icon: ShieldCheck, tone: 'rose' },
  { key: 'duplicate_sessions',           label: 'Duplicate Sessions (occurrence_key)', icon: Copy, tone: 'rose' },
  { key: 'orphan_prescriptions',         label: 'Orphan Prescriptions (scheduled, no canonical)', icon: Ghost, tone: 'rose' },
];

const TONE_CLS = {
  zinc: 'bg-zinc-800/40 border-zinc-700 text-zinc-200',
  blue: 'bg-blue-500/10 border-blue-500/30 text-blue-300',
  emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300',
  indigo: 'bg-indigo-500/10 border-indigo-500/30 text-indigo-300',
  amber: 'bg-amber-500/10 border-amber-500/30 text-amber-300',
  rose: 'bg-rose-500/10 border-rose-500/30 text-rose-300',
};

export default function IntegrityDashboardPage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [openKey, setOpenKey] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const res = await api.get('/superadmin/integrity/prescription-pipeline');
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load integrity dashboard');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center min-h-[400px] text-zinc-400" data-testid="integrity-loading">
        <Loader2 className="w-6 h-6 mr-2 animate-spin" /> Loading integrity report…
      </div>
    );
  }

  const totalIssues = ISSUES.reduce((n, i) => n + (i.flat ? (data[i.key] || 0) : (data[i.key]?.count || 0)), 0);

  return (
    <div className="space-y-6" data-testid="integrity-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-zinc-100">Prescription Pipeline Integrity</h1>
          <p className="text-xs text-zinc-500 mt-1">Read-only diagnostic of the Prescription → Planner/RTP → Analytics pipeline.</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" data-testid="integrity-total-issues"
            className={totalIssues === 0 ? 'border-emerald-500/40 text-emerald-300' : 'border-rose-500/40 text-rose-300'}>
            {totalIssues === 0 ? 'All green' : `${totalIssues} issue(s)`}
          </Badge>
          <Button onClick={load} size="sm" variant="outline" className="border-zinc-700 text-zinc-200 hover:bg-zinc-800" data-testid="integrity-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* Aggregate counters */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {METRICS.map(m => {
          const Icon = m.icon;
          return (
            <Card key={m.key} className={`${TONE_CLS[m.tone]} border`}
              data-testid={`integrity-metric-${m.key}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Icon className="w-5 h-5 opacity-80" />
                  <span className="text-2xl font-bold" data-testid={`integrity-metric-${m.key}-value`}>{data[m.key] ?? 0}</span>
                </div>
                <div className="text-xs font-semibold uppercase tracking-wide">{m.label}</div>
                <div className="text-[10px] opacity-70 mt-1">{m.detail}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Issue cards with drill-down */}
      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-zinc-300 uppercase tracking-wider">Integrity Issues</h2>
        {ISSUES.map(i => {
          const count = i.flat ? (data[i.key] || 0) : (data[i.key]?.count || 0);
          const sample = i.flat ? [] : (data[i.key]?.sample || []);
          const isOpen = openKey === i.key;
          const Icon = i.icon;
          const ok = count === 0;
          return (
            <Card key={i.key} className={ok ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-rose-500/5 border-rose-500/30'}
              data-testid={`integrity-issue-${i.key}`}>
              <CardHeader
                className="cursor-pointer py-3"
                onClick={() => !ok && setOpenKey(isOpen ? null : i.key)}
                data-testid={`integrity-issue-${i.key}-toggle`}
              >
                <CardTitle className="flex items-center justify-between text-sm font-medium">
                  <span className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${ok ? 'text-emerald-400' : 'text-rose-400'}`} />
                    {i.label}
                  </span>
                  <span className="flex items-center gap-2">
                    <Badge className={ok ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' : 'bg-rose-500/20 text-rose-300 border-rose-500/40'}
                      data-testid={`integrity-issue-${i.key}-count`}>
                      {count}
                    </Badge>
                    {!ok && (isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />)}
                  </span>
                </CardTitle>
              </CardHeader>
              {isOpen && !ok && (
                <CardContent className="pt-0 pb-4 space-y-1" data-testid={`integrity-issue-${i.key}-samples`}>
                  {sample.length === 0 ? (
                    <div className="text-xs text-zinc-500 italic">No sample records</div>
                  ) : sample.map((s, idx) => (
                    <div key={idx} className="text-xs bg-zinc-900/40 rounded px-2 py-1.5 flex flex-wrap items-center justify-between gap-2">
                      <span className="text-zinc-300">
                        {s.title || s._id || s.id}
                        {s.date && <span className="text-zinc-500 ml-2">{s.date}</span>}
                        {s.week_start && <span className="text-zinc-500 ml-2">week {s.week_start}</span>}
                        {s.n && <span className="text-rose-400 ml-2">×{s.n}</span>}
                      </span>
                      <span className="flex items-center gap-1">
                        {s.id && i.key !== 'orphan_prescriptions' && (
                          <Link to={`/staff/planner/session/${s.id}`} className="text-indigo-400 hover:text-indigo-300 text-[11px]" data-testid={`integrity-drilldown-session-${s.id}`}>
                            open session →
                          </Link>
                        )}
                        {s.id && i.key === 'orphan_prescriptions' && (
                          <Link to={`/staff/prescriptions/${s.id}`} className="text-amber-400 hover:text-amber-300 text-[11px]" data-testid={`integrity-drilldown-prescription-${s.id}`}>
                            open prescription →
                          </Link>
                        )}
                        {i.key === 'duplicate_sessions' && s.ids?.length > 0 && (
                          <span className="text-[10px] text-zinc-500">ids: {s.ids.slice(0, 3).join(', ')}{s.ids.length > 3 ? '…' : ''}</span>
                        )}
                      </span>
                    </div>
                  ))}
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
